import AddIcon from '@mui/icons-material/Add';
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Stack,
	TextField,
	Typography,
	useTheme
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useAppConfig } from 'api/appConfig';
import { useRoles } from 'api/attributes';
import { useCurrentGuideIdP } from 'api/idps';
import { GENERIC_IDP_ID, GuideType, Method } from 'consts';
import {
	useCurrentGuideIdPID,
	useIdPConfigContext
} from 'contexts/IdPConfigContext';
import {
	useCurrentGuideMethod,
	useSSOConfigMappings
} from 'contexts/SSOConfigFieldsContext';
import { ID, useMappingEditor } from 'hooks/useMappingEditor';
import React, { useCallback, useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { IGroupsAttributeMapping, S4Feature } from 'types/api';
import { FeatureFlagId } from 'types/featureflags';
import {
	useFeatureEnabledForConfig,
	useIsAuthenticationOnly
} from 'api/configurations';
import EnabledFeatureCheck from 'components/EnabledFeatureCheck';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import { isGroupMappingDisabled } from 'utils/s4FeaturesUtils';
import MDGapWrapper from '../MDRenderer/utilComponents/MDGapWrapper';
import DefaultSSORolesField from './DefaultSSORolesField';
import FGAMapping from './FGAMapping';
import GroupsAttributeNameField from './GroupsAttributeNameField';
import MappingRow from './MappingRow';
import {
	RoleOption,
	filterRoleOptions,
	renderRoleOption,
	toRoleOptions
} from './roleOptions';

const GroupsAttributeMapping: React.FC = () => {
	const {
		palette: {
			grey: { 700: grey700 }
		}
	} = useTheme();

	// Guide-type-aware idp/method so the group mapping initializes in the XAA/SCIM
	// contexts too (the raw SSO idpID/method are null there).
	const { guideType } = useIdPConfigContext();
	const isSCIMGuide = guideType === GuideType.SCIM;
	const idpID = useCurrentGuideIdPID();
	const method = useCurrentGuideMethod();
	const { data: idpData } = useCurrentGuideIdP();
	const { isFeatureFlagActive } = useFeatureFlags();
	const { data: authenticationOnly } = useIsAuthenticationOnly();
	const { data: appConfig } = useAppConfig();
	const { enabled: roleMappingEnabled } = useFeatureEnabledForConfig(
		S4Feature.RoleMapping
	);
	const { enabled: fgaMappingEnabled } = useFeatureEnabledForConfig(
		S4Feature.FgaMapping
	);

	const {
		isLoading: isRolesLoading,
		isIdle: isRolesIdle,
		data: rolesData
	} = useRoles();

	const rolesOptions = useMemo<RoleOption[]>(
		() => toRoleOptions(rolesData),
		[rolesData]
	);

	const { getGroupsAttributeMappings, setGroupsAttributeMappings } =
		useSSOConfigMappings();

	const createEmptyItem = useCallback(
		(): IGroupsAttributeMapping => ({ group: null, roleId: null }),
		[]
	);

	const { rows, addItem, editItem, deleteItem, persist, isReady } =
		useMappingEditor<IGroupsAttributeMapping>({
			initialItems:
				rolesData && idpID && method
					? (getGroupsAttributeMappings() ?? [])
					: null,
			setExternalItems: setGroupsAttributeMappings,
			createEmptyItem,
			enabled: !!(rolesData && idpID && method)
		});

	const showLoader = isRolesLoading || isRolesIdle || !isReady;

	const isOidcGroupMappingHidden =
		method === Method.Oidc &&
		!isFeatureFlagActive(FeatureFlagId.SSO_OIDC_GROUP_MAPPING);

	// the attribute carrying groups is SSO-specific — in SCIM guides groups
	// arrive via /scim/v2/Groups, so there is no attribute name to configure
	//
	// On an authentication-only connection the attribute name still matters - the groups it carries
	// reach the flow - even though role and FGA mapping are off. A suite that turned group mapping
	// off still wins: roleMappingEnabled/fgaMappingEnabled read false for either reason, so only the
	// raw features tell the two apart.
	const showGroupsAttributeName =
		!isSCIMGuide &&
		!isOidcGroupMappingHidden &&
		!isGroupMappingDisabled(appConfig?.features) &&
		(roleMappingEnabled || fgaMappingEnabled || !!authenticationOnly);

	return (
		<MDGapWrapper>
			{showGroupsAttributeName && <GroupsAttributeNameField />}
			<EnabledFeatureCheck feature={S4Feature.RoleMapping}>
				{!isOidcGroupMappingHidden && (
					<Stack gap={2} mt={3}>
						<Stack>
							<Typography variant="h3">
								{intl.tr('ssoConfig.fields.groupsMapping.header')}
							</Typography>
							<Typography variant="body1">
								{intl.tr(
									isSCIMGuide
										? 'ssoConfig.fields.groupsMapping.scimSubHeader'
										: 'ssoConfig.fields.groupsMapping.subHeader',
									{
										idpName: idpData?.name,
										isGeneric: idpData?.id === GENERIC_IDP_ID
									}
								)}
							</Typography>
						</Stack>
						{showLoader ? (
							<Stack justifyContent="center" alignItems="center" height={200}>
								<CircularProgress data-testid="loader" />
							</Stack>
						) : (
							<Box>
								<Collapse in={rows.length > 0}>
									<Typography variant="body1">
										{intl.tr(
											'ssoConfig.fields.groupsMapping.mappingDescription'
										)}
									</Typography>
								</Collapse>
								<Stack gap={1}>
									<TransitionGroup>
										{rows.map((row, i) => (
											<Collapse key={row[ID]}>
												<MappingRow
													onDelete={
														/* istanbul ignore next  */
														() => deleteItem(i)
													}
												>
													<TextField
														size="small"
														label={intl.tr(
															'ssoConfig.fields.groupsMapping.groupLabel',
															{
																idpName: idpData?.name,
																isGeneric: idpData?.id === GENERIC_IDP_ID
															}
														)}
														value={row.group ?? ''}
														onChange={(e) =>
															editItem(i, 'group', e.target.value || null)
														}
														onBlur={persist}
														sx={{ flexGrow: 1, flexBasis: 0 }}
														slotProps={{ inputLabel: { shrink: true } }}
													/>
													<Typography color={grey700} mt={1}>
														{intl.tr('ssoConfig.fields.mapping.mapTo')}
													</Typography>
													<Autocomplete<RoleOption>
														size="small"
														options={rolesOptions}
														value={
															rolesOptions.find(
																(o) => o.value === row.roleId
															) ?? null
														}
														getOptionLabel={(opt) =>
															typeof opt === 'string' ? opt : (opt?.label ?? '')
														}
														isOptionEqualToValue={
															/* istanbul ignore next  */
															(opt, val) => opt?.value === val?.value
														}
														filterOptions={filterRoleOptions}
														renderOption={renderRoleOption}
														onChange={(_, newValue) => {
															editItem(
																i,
																'roleId',
																newValue?.value ?? null,
																true
															);
														}}
														sx={{ flexGrow: 1, flexBasis: 0 }}
														renderInput={(params) => (
															<TextField
																{...params}
																data-testid="attribute-mapping-editor-dest-input"
																label={intl.tr(
																	'ssoConfig.fields.groupsMapping.roleLabel'
																)}
																slotProps={{
																	inputLabel: { shrink: true }
																}}
															/>
														)}
													/>
												</MappingRow>
											</Collapse>
										))}
									</TransitionGroup>
									<Box>
										<Button
											startIcon={<AddIcon />}
											onClick={addItem}
											data-testid="attribute-mapping-editor-add-button"
										>
											<Typography variant="body1">
												{intl.tr('ssoConfig.fields.groupsMapping.addMapping')}
											</Typography>
										</Button>
									</Box>
								</Stack>
							</Box>
						)}
						<Typography variant="body1">
							{intl.tr('ssoConfig.fields.defaultSSORoles.line')}
						</Typography>
						<DefaultSSORolesField />
					</Stack>
				)}
			</EnabledFeatureCheck>

			{!isOidcGroupMappingHidden && (
				<EnabledFeatureCheck feature={S4Feature.FgaMapping}>
					<FGAMapping />
				</EnabledFeatureCheck>
			)}
		</MDGapWrapper>
	);
};

export default GroupsAttributeMapping;
